import type { BrandGroup, Partner } from '@provision/entity-types';
import { resolveSupportPhone } from '@provision/formatting-utils';
import { formatToPhoneNumber } from 'src/components/FormattedPhoneInput/format-to-phone-number';
import { NullableDeepPartial } from 'src/graphql/types';

type PartnerTabBrandGroup = NullableDeepPartial<Pick<
  BrandGroup,
  | 'brandGroupId'
  | 'fwdPhonePublished'
  | 'fwdPhonePublishedExt'
  | 'fwdPhoneSupport'
  | 'supportEmail'
  | 'membershipEmail'
>>;

export type PartnerTabContactPartner = NullableDeepPartial<Pick<
  Partner,
  'supportPhone' | 'supportEmail' | 'membershipEmail' | 'supportLiveDate' | 'litsDate' | 'partnerOnesheet' | 'partnerId' | 'brandGroups'
>>;

export function getPartnerTabBrandGroup(
  partner: PartnerTabContactPartner,
  brandGroupId?: string | null,
  _hasAddressBrand = false,
): PartnerTabBrandGroup | null {
  const brandGroups = partner?.brandGroups ?? [];
  const matchingBrandGroup = brandGroupId
    ? brandGroups.find((brandGroup) => brandGroup?.brandGroupId === brandGroupId)
    : null;

  return matchingBrandGroup ?? brandGroups[0] ?? null;
}

export function resolvePartnerTabContactInfo<TPartner extends PartnerTabContactPartner>(
  partner: TPartner | null | undefined,
  brandGroupId?: string | null,
  hasAddressBrand = false,
): TPartner | null {
  if (!partner) {
    return null;
  }

  const brandGroup = getPartnerTabBrandGroup(partner, brandGroupId, hasAddressBrand);

  const brandGroupSupportPhone = resolveSupportPhone({
    fwdPhonePublished: brandGroup?.fwdPhonePublished,
    fwdPhonePublishedExt: brandGroup?.fwdPhonePublishedExt,
    fwdPhoneSupport: brandGroup?.fwdPhoneSupport,
    formatPhoneNumber: formatToPhoneNumber,
  });
  const supportPhone =
    brandGroupSupportPhone ??
    (partner.supportPhone ? formatToPhoneNumber(String(partner.supportPhone)) : '');

  return {
    ...partner,
    supportPhone,
    supportEmail: brandGroup?.supportEmail ?? partner.supportEmail ?? '',
    membershipEmail: brandGroup?.membershipEmail ?? partner.membershipEmail ?? '',
  };
}
